import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { CreateChatContentDto } from "@/dto/ChatContentDto";
import { Yn } from "@/constants/code";

/**
 * 카테고리 옵션
 */
const categoryOptions = [
  { value: "GENERAL", label: "일반 문의" },
  { value: "REGISTRATION", label: "등록 관련" },
  { value: "SCHEDULE", label: "수업 일정" },
  { value: "PAYMENT", label: "결제 관련" },
  { value: "FACILITY", label: "시설 안내" },
  { value: "PROGRAM", label: "프로그램 안내" },
  { value: "EVENT", label: "이벤트" },
  { value: "CONTACT", label: "연락처" },
  { value: "OTHER", label: "기타" },
];

/**
 * 액션 타입 옵션 (3개 항목으로 제한)
 */
const actionTypeOptions = [
  { value: "LINK", label: "링크 이동" },
  { value: "PHONE", label: "전화 걸기" },
  { value: "EMAIL", label: "이메일 보내기" },
];

/**
 * 채팅 컨텐츠 생성 폼 Hook
 * @description 채팅 컨텐츠 생성을 위한 폼 로직을 관리합니다.
 * @author Max <sud665@ymjlab.com>
 * @version 1.0.0
 * @since 2025-01-16
 */
export default function useCreateForm(
  onSubmit: (data: CreateChatContentDto) => void
) {
  const methods = useForm<CreateChatContentDto>({
    resolver: yupResolver(
      yup.object().shape({
        category: yup.string().required("카테고리를 선택해주세요."),
        content: yup.string().required("컨텐츠 내용을 입력해주세요."),
        keywords: yup.string().optional(),
        actionType: yup.string().optional(),
        actionUrl: yup
          .string()
          .optional()
          .test(
            "url-validation",
            "올바른 URL 형식을 입력해주세요.",
            function (value) {
              if (!value) return true; // 선택사항이므로 빈 값은 허용
              try {
                new URL(value);
                return true;
              } catch {
                return false;
              }
            }
          ),
        actionText: yup.string().optional(),
        actionDescription: yup.string().optional(),
        useYn: yup.mixed<Yn>().oneOf([Yn.Y, Yn.N]).default(Yn.Y),
      })
    ),
    defaultValues: {
      category: "",
      content: "",
      keywords: "",
      actionType: "",
      actionUrl: "",
      actionText: "",
      actionDescription: "",
      useYn: Yn.Y,
    },
  });

  /**
   * 폼 제출 핸들러
   */
  const handleSubmit = methods.handleSubmit((data) => {
    // 빈 문자열을 undefined로 변환
    const cleanedData: CreateChatContentDto = {
      ...data,
      keywords: data.keywords?.trim() || undefined,
      actionType: data.actionType?.trim() || undefined,
      actionUrl: data.actionUrl?.trim() || undefined,
      actionText: data.actionText?.trim() || undefined,
      actionDescription: data.actionDescription?.trim() || undefined,
    };

    onSubmit(cleanedData);
  });

  return {
    methods,
    handleSubmit,
    categoryOptions,
    actionTypeOptions,
  };
}
